import React, { useState, useEffect } from 'react';
import { AlertCircle, Lock, Mail, Trash2, Clock, CheckCircle, X } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface DeleteSchedule {
  status: 'pending' | 'scheduled' | 'none';
  scheduledDate?: string;
  daysRemaining?: number;
}
const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToDelete, setAgreeToDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteSchedule, setDeleteSchedule] = useState<DeleteSchedule>({ status: 'none' });
  const [canceling, setCanceling] = useState(false);

  // Check deletion status on mount
  useEffect(() => {
    checkDeletionStatus();
  }, []);

  // Calculate business days remaining
  const calculateBusinessDaysRemaining = (scheduledDate: string): number => {
    const today = new Date();
    const scheduled = new Date(scheduledDate);
    let businessDays = 0;
    let current = new Date(today);

    while (current < scheduled) {
      current.setDate(current.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (current.getDay() !== 0 && current.getDay() !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  };

  const checkDeletionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/user/deletion-status', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.deletionScheduled) {
          const daysRemaining = calculateBusinessDaysRemaining(data.scheduledDate);
          setDeleteSchedule({
            status: 'scheduled',
            scheduledDate: data.scheduledDate,
            daysRemaining,
          });
        }
      }
    } catch (error) {
      console.error('Error checking deletion status:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!agreeToDelete) {
      newErrors.agree = 'Please confirm you want to delete your account';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call your delete account API
      const response = await fetch('/api/user/delete-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to schedule account deletion');
      }

      const data = await response.json();
      
      // Update deletion schedule
      const daysRemaining = calculateBusinessDaysRemaining(data.scheduledDate);
      setDeleteSchedule({
        status: 'scheduled',
        scheduledDate: data.scheduledDate,
        daysRemaining,
      });

      toast.success('Account deletion scheduled! Your account will be deleted in 7 business days.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to schedule account deletion. Please try again.');
      console.error('Delete account error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!window.confirm('Are you sure you want to cancel the deletion? Your account will be restored.')) {
      return;
    }

    setCanceling(true);

    try {
      const response = await fetch('/api/user/cancel-deletion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to cancel deletion');
      }

      setDeleteSchedule({ status: 'none' });
      toast.success('Deletion cancelled! Your account has been restored.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel deletion. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Scheduled Deletion Alert */}
          {deleteSchedule.status === 'scheduled' && (
            <div className="mb-8 bg-green-50 border-l-4 border-green-600 rounded-lg p-6 shadow-md">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-bold text-green-900 text-lg mb-2">Account Deletion Scheduled ✓</h3>
                  <p className="text-green-800 mb-3">
                    Your account has been scheduled for deletion on <span className="font-bold">{new Date(deleteSchedule.scheduledDate!).toLocaleDateString()}</span>
                  </p>
                  <div className="bg-green-100 rounded p-3 mb-4">
                    <p className="text-green-900 font-semibold text-lg">
                      {deleteSchedule.daysRemaining} Business Days Remaining
                    </p>
                  </div>
                  <p className="text-sm text-green-700 mb-4">
                    You can still cancel the deletion within this period. Your account will remain accessible until the scheduled date.
                  </p>
                  <button
                    onClick={handleCancelDeletion}
                    disabled={canceling}
                    className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition"
                  >
                    <X className="w-4 h-4" />
                    {canceling ? 'Canceling...' : 'Cancel Deletion'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          {deleteSchedule.status !== 'scheduled' && (
            <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Delete Your Account</h1>
              <p className="text-gray-600 text-lg">This action cannot be undone</p>
            </div>

            {/* Warning Card */}
            <div className="bg-white rounded-lg shadow-md mb-8 p-6 border-l-4 border-red-500">
            <div className="flex gap-4">
              <Clock className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Account Deletion Notice</h3>
                <p className="text-gray-700 mb-3">
                  Your account will be permanently deleted <span className="font-bold text-red-600">24 hours</span> after confirmation.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded p-3">
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span>Account will be deleted after 7 business days</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span>All personal data, bookings, and history will be permanently removed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span>You can cancel the deletion anytime before the scheduled date</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">•</span>
                      <span>After deletion, you won't be able to recover your account or data</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            </div>

            {/* Delete Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleDeleteAccount} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-3">
                  <Mail className="inline w-4 h-4 mr-2" />
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors({ ...errors, email: '' });
                    }
                  }}
                  placeholder="Enter your email"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none transition ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-3">
                  <Lock className="inline w-4 h-4 mr-2" />
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors({ ...errors, password: '' });
                      }
                    }}
                    placeholder="Enter your password"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none transition ${
                      errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirmation Checkbox */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToDelete}
                    onChange={(e) => {
                      setAgreeToDelete(e.target.checked);
                      if (errors.agree) {
                        setErrors({ ...errors, agree: '' });
                      }
                    }}
                    className="w-5 h-5 text-red-600 border-gray-300 rounded mt-1 cursor-pointer"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">
                      I understand that my account will be permanently deleted
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      All my data including profile, bookings, and history will be removed and cannot be recovered.
                    </p>
                  </div>
                </label>
                {errors.agree && (
                  <p className="text-red-600 text-sm mt-3 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.agree}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-900 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !agreeToDelete}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition ${
                    loading || !agreeToDelete
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 active:scale-95'
                  }`}
                >
                  <Trash2 className="w-5 h-5" />
                  {loading ? 'Processing...' : 'Delete Account'}
                </button>
              </div>

              {/* Info Text */}
              <p className="text-center text-sm text-gray-600 pt-4 border-t">
                Need help? <a href="mailto:support@metafitwellness.com" className="text-teal-600 hover:underline font-semibold">
                  Contact Support
                </a>
              </p>
            </form>
            </div>

            {/* Timeline */}
            <div className="mt-8 bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold">1</div>
                    <div className="w-1 h-16 bg-gray-300 mt-2"></div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Deletion Request Submitted</p>
                    <p className="text-sm text-gray-600">Your account deletion request has been scheduled</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                    <div className="w-1 h-16 bg-gray-300 mt-2"></div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">7 Business Days Waiting Period</p>
                    <p className="text-sm text-gray-600">You have 7 business days to cancel the deletion. Your account remains accessible during this period.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-semibold text-gray-900">Permanent Deletion</p>
                    <p className="text-sm text-gray-600">After 7 business days, your account and all associated data will be permanently deleted from our systems.</p>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DeleteAccount;
